import {useEffect, useRef, useState, useCallback} from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import {doc, getFirestore, onSnapshot} from "firebase/firestore";
import {io} from 'socket.io-client';
import MRIViewport from "./MRIViewport";
import {NVImage, SLICE_TYPE} from "@niivue/niivue";
import LUT from "./niivue_LUT.json";
import ThreeViewport from "./ThreeViewport";
import BreadCrumbsWithIcons from "../../shared/components/BreadCrumbsWithIcons";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleOutlineRoundedIcon from '@mui/icons-material/PeopleOutlineRounded';
import PersonIcon from '@mui/icons-material/Person';
import UndoIcon from '@mui/icons-material/Undo';
import {useNavigate, useLocation} from "react-router-dom";
import { usePatientProgress, getErrorLabel } from "../../patient-details/hooks/usePatientProgress";
import { useOfflineAssetUrls } from '../hooks/useOfflineAssetUrls';
import { usePatientStatus } from '../hooks/usePatientStatus';
import ProcessingErrorDialog from "../../shared/components/ProcessingErrorDialog";
import PatientProcessingStepper from "./PatientProcessingStepper";
import { DRACOLoader, GLTFLoader } from 'three-stdlib';
import { getSignedDownloadUrl } from "../../services/storageService";

const usersDbName = `${import.meta.env.VITE_USERS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`
const operationsDbName = `${import.meta.env.VITE_OPERATIONS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`
import { useDashboardAuthState } from '../hooks/useDashboardAuthState';

const MAX_TOKEN_FETCH_ATTEMPTS = 2;
const TOKEN_FETCH_RETRY_DELAY_MS = 500;



export const CORRECTION_SOCKET_RECONNECTION_ATTEMPTS = 12;
export const CORRECTION_MAX_RECONNECT_CYCLES = 5;
// Shown when the correction socket fails unrecoverably (auth failure, transport down, etc.)
// and the user must restart the session.
export const CORRECTION_SESSION_ENDED_MESSAGE =
    'Your correction session ended. Click the edit button to restart it.';

// Selected-state background colors for the correction sidebar controls. Fixed
// design-spec values. They are intentionally literal rgba,
// not theme palette tokens (success/error/action.selected) - do not substitute.
export const CORRECTION_POSITIVE_SELECTED_BG = 'rgba(0, 188, 56, 0.6)';
export const CORRECTION_NEGATIVE_SELECTED_BG = 'rgba(231, 11, 0, 0.6)';
export const CORRECTION_LABEL_SELECTED_BG = 'rgba(140, 155, 169, 0.6)';

/**
 * `sx` fragment that paints a control's selected state.
 * Shared by the three correction-sidebar controls.
 * @param {string} color
 */
const selectedBackgroundSx = (color) => ({
    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: color },
});

export default function RenderVolumeCard() {
    const navigate = useNavigate();
    const location = useLocation();
    const offlinePatientId = location.state?.offlinePatientId || null;
    const isOfflineView = !!offlinePatientId;
    const isOfflineMode = isOfflineView;
    const running = useRef(false)
    const [patientId, setPatientId] = useState('');
    const [patientResolved, setPatientResolved] = useState(false);
    const [labels, setLabels] = useState(/** @type {number[]} */ ([]));
    const [selectedLabel, setSelectedLabel] = useState(0);
    const selectedLabelRef = useRef(selectedLabel);
    const [clickPositive, setClickPositive] = useState(true);
    const clickPositiveRef = useRef(clickPositive);
    const labelTemplate = useRef(/** @type {Record<string, string> | null} */ (null));

    /** @param {{ uid: string }} user */
    const subscribeToPatientId = useCallback((user) => {
        const db = getFirestore(usersDbName);
        if (patientDocUnsubscribeRef.current) {
            patientDocUnsubscribeRef.current();
        }
        patientDocUnsubscribeRef.current = onSnapshot(doc(db, "users", user.uid), (document) => {
            const userInfo = document.data();
            setPatientId(userInfo?.patientId || '');
            setPatientResolved(true);
        });
    }, []);

    const { authChecked, isDemoUser, isDemoOffline, userId, getIdToken } = useDashboardAuthState(
        offlinePatientId,
        subscribeToPatientId,
        setPatientId,
        setPatientResolved,
    );

    const imgVolRef = useRef(/** @type {any} */ (null));
    const imgVolZerosLikeRef = useRef(/** @type {any} */ (null));
    const [imgVolId, setImgVolId] = useState(/** @type {string | null} */ (null));
    const segVolRef = useRef(/** @type {any} */ (null));
    const [segVolId, setSegVolId] = useState(/** @type {string | null} */ (null));
    const objRef = useRef(/** @type {any} */ (null));
    const [objId, setObjId] = useState(/** @type {any} */ (null));
    const [isDownloadingImages, setIsDownloadingImages] = useState(false);
    const [isDownloadingModel, setIsDownloadingModel] = useState(false);
    const [maximized, setMaximized] = useState(-1);
    const [processingClick, setProcessingClick] = useState(false);
    const [undoEnabled, setUndoEnabled] = useState(false);
    const [clickCoords, setClickCoords] = useState([0, 0, 0]);
    const processingClickRef = useRef(processingClick);

    const handleCorrectionUpdateComplete = () => {
        if (processingClickRef.current) {
            setProcessingClick(false);
            processingClickRef.current = false;
        }
    };
   
    const clickSocket = useRef(/** @type {any} */ (null));
    const clickSocketInstanceRef = useRef(/** @type {any} */ (null));
    const reconnectFailedHandlerRef = useRef(/** @type {null | (() => void)} */ (null));
    const reconnectCyclesRef = useRef(0);
    const startingClickModuleRef = useRef(false);
    const [loadingClickModule, setLoadingClickModule] = useState(false);
    const [correctionModuleLoaded, setCorrectionModuleLoaded] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [isDownloadErrorDialogOpen, setIsDownloadErrorDialogOpen] = useState(false);
    const [isValidateConfirmOpen, setIsValidateConfirmOpen] = useState(false);
    const [isAuthErrorSnackbarOpen, setIsAuthErrorSnackbarOpen] = useState(false);
    const [authErrorMessage, setAuthErrorMessage] = useState('');
    const [clickInformation, setClickInformation] = useState(/** @type {{position: number[] | null, value: number | null}} */ ({position: null, value: null}));
    const clickHistoryRef = useRef(/** @type {{position: number[], value: number}[]} */ ([]));
    const [markerRedrawSignal, setMarkerRedrawSignal] = useState(0);
    const [isCorrecting, setIsCorrecting] = useState(false);
    const assumeOfflineComplete = !!offlinePatientId;
    const { currentStepLabel, currentStepKey, percentage, isError: isProgressError } = usePatientProgress(patientId, assumeOfflineComplete);
    const { offlineImageUrl, offlineSegUrl, offlineModelUrl } = useOfflineAssetUrls(patientId, offlinePatientId);
    const {
        patientName,
        segmentationDone,
        segmentationValidated,
        modelExported,
        nervesRecognition,
        segmentedStructure,
        diffusionSpacing,
        patientStatusLoaded,
        isError: isPatientStatusError,
    } = usePatientStatus(patientId, offlinePatientId);
    // usePatientStatus's isError only reflects the separate Errors-DB subscription;
    const isError = isPatientStatusError || isProgressError;
    const layoutReady = !!patientId && patientStatusLoaded;
    const showFourViewportGrid = !isDemoUser && patientStatusLoaded && maximized === -1 && !modelExported;
    // Not in a viewable/actionable state yet (before/during segmentation, or post-
    // validation while the 3D model is being built) - show the pipeline Stepper
    // instead of the MRI/3D panels, so that it (rather than the broken/black MRI
    // panels) sits behind ProcessingErrorDialog when an error occurs mid-pipeline.
    // Never intercepts the correction viewer (needsCorrection) or the completed 3D
    // viewer (modelExported) - those remain the backdrop for an error at that stage.
    const needsCorrection = segmentationDone && !segmentationValidated;
    const isProcessingUnviewable =
        layoutReady && !isDemoUser && !isOfflineView && !needsCorrection && !modelExported;

    const patientDocUnsubscribeRef = useRef(/** @type {null | (() => void)} */ (null));
    const segmentationApiUrl = import.meta.env.VITE_SEGMENTATION_API_URL;
    const cancelledRef = useRef(false);
    const shouldLoadModel = isDemoUser ? segmentationDone : modelExported;
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(`${import.meta.env.PUBLIC_URL || ''}/shared-assets/draco-decoder/`);

    /**
     * Clears the "a correction click is being processed" state. If the socket
     * drops between sendClick() and 'correction_done', that reply never arrives,
     * so without this the [clickCoords] guard swallows all further clicks and
     * the Validate button stays disabled until a full reload.
     */
    const resetInFlightClick = () => {
        if (!processingClickRef.current) return;
        processingClickRef.current = false;
        setProcessingClick(false);
        setIsDownloadingImages(false);
    };

    /**
     * Fully tears down a correction socket instance: removes our listeners
     * (socket + manager), stops the reconnect loop, clears both refs if they
     * still point at this socket, and resets the loading/correcting flags.
     * Idempotent - safe to call again with the same or a stale socket.
     * @param {any} socket
     */
    const teardownCorrectionSocket = (socket) => {
        if (!socket) return;
        // Only touch session UI state if this is the socket the current session
        const wasCurrent = clickSocketInstanceRef.current === socket;
        socket.off('connect');
        socket.off('ready');
        socket.off('correction_done');
        socket.off('disconnect');
        socket.off('connect_error');
        if (socket.io && reconnectFailedHandlerRef.current) {
            socket.io.off('reconnect_failed', reconnectFailedHandlerRef.current);
        }
        socket.disconnect();
        if (clickSocketInstanceRef.current === socket) clickSocketInstanceRef.current = null;
        if (clickSocket.current === socket) clickSocket.current = null;
        if (wasCurrent) {
            // Genuine end of session - reset the sidebar
            reconnectFailedHandlerRef.current = null;
            setIsCorrecting(false);
            setLoadingClickModule(false);
            setLabels([]);
            setSelectedLabel(0);
            selectedLabelRef.current = 0;
            setClickPositive(true);
            clickPositiveRef.current = true;
            setCorrectionModuleLoaded(false);
            setUndoEnabled(false);
            clickHistoryRef.current = [];
            resetInFlightClick();
        }
    };

    const cleanupClickSocket = () => {
        teardownCorrectionSocket(clickSocketInstanceRef.current);
    };

    /** @param {string} context @param {unknown} err */
    const showAuthErrorSnackbar = (context, err) => {
        console.error(`Failed to obtain auth token to ${context}`, err);
        setAuthErrorMessage('Unable to verify your session. Please try again.');
        setIsAuthErrorSnackbarOpen(true);
    };

    /**
     * Surfaces a signed-URL download failure via the ProcessingErrorDialog
     * (errorType="download"), then rethrows so the caller's existing
     * try/catch/finally still resets its loading state.
     * @param {string} context @param {unknown} err
     */
    const showDownloadErrorDialog = (context, err) => {
        console.error(`Failed to ${context}`, err);
        setIsDownloadErrorDialogOpen(true);
    };

    /**
     * Resolves a fresh ID token, or surfaces the auth-error Snackbar and
     * returns null - treating "no token" (getIdToken resolved null, e.g. no
     * signed-in user) the same as a rejected fetch, so callers never proceed
     * unauthenticated. Retries once after a transient failure (e.g. a
     * network blip during token refresh) before giving up, since a single
     * getIdToken() failure does not necessarily mean the user is signed out.
     * @param {string} context
     * @returns {Promise<string | null>}
     */
    const requireIdToken = async (context) => {
        for (let attempt = 1; attempt <= MAX_TOKEN_FETCH_ATTEMPTS; attempt++) {
            try {
                const idToken = await getIdToken();
                if (idToken) return idToken;
                if (attempt === MAX_TOKEN_FETCH_ATTEMPTS) {
                    showAuthErrorSnackbar(context, new Error('No authenticated user'));
                    return null;
                }
            } catch (err) {
                if (attempt === MAX_TOKEN_FETCH_ATTEMPTS) {
                    showAuthErrorSnackbar(context, err);
                    return null;
                }
            }
            await new Promise((resolve) => setTimeout(resolve, TOKEN_FETCH_RETRY_DELAY_MS));
        }
        return null;
    };

    const getImageUrl = async () => {
        if (isOfflineView) {
            return offlineImageUrl ?? '';
        }
        try {
            return await getSignedDownloadUrl({ asset: 'mri_volume', patientId });
        } catch (err) {
            showDownloadErrorDialog('load the MRI volume', err);
            throw err;
        }
    };

    const getSegmentationUrl = async () => {
        if (isOfflineView) {
            return offlineSegUrl ?? '';
        }
        try {
            return await getSignedDownloadUrl({ asset: 'segmentation', patientId });
        } catch (err) {
            showDownloadErrorDialog('load the segmentation', err);
            throw err;
        }
    };

    const getModelGLBUrl = async () => {
        if (isOfflineView) {
            return offlineModelUrl ?? '';
        }
        try {
            return await getSignedDownloadUrl({ asset: 'model', patientId });
        } catch (err) {
            showDownloadErrorDialog('load the 3D model', err);
            throw err;
        }
    }

    /** @param {number} label */
    const selectLabel = (label) => {
        setSelectedLabel(label);
        selectedLabelRef.current = label;
    }

    /** @param {any} e @param {boolean | null} newType */
    const handleClickTypeChange = (e, newType) => {
        if (newType === null) return;
        setClickPositive(newType);
        clickPositiveRef.current = newType;
    }

    const validateSegmentation = async () => {
        const idToken = await requireIdToken('validate this segmentation');
        if (cancelledRef.current) return;
        if (!idToken) return;

        const headers = {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': `https://${import.meta.env.VITE_ENVIRONMENT}.replico.tech`,
            'Authorization': `Bearer ${idToken}`,
        };

        const requestOptions = {
            method: 'POST',
            headers,
            body: JSON.stringify({
                patient_id: patientId, user_id: userId, structure: segmentedStructure,
                nerves: nervesRecognition, diff_spacing: diffusionSpacing
            })
        };
        const response = await fetch(`${segmentationApiUrl}/validate-segmentation`, requestOptions);
        await response.text();
        if (response.ok) {
            cleanupClickSocket();
            await reRenderSeg();
        }
    }

    const handleConfirmValidate = () => {
        setIsValidateConfirmOpen(false);
        validateSegmentation();
    };

    let sendClick = () => {
        if (!clickSocket.current) return;
        clickSocket.current.emit('click', {
            click_pos: Array.from(clickCoords),
            positive: clickPositiveRef.current,
            structure: selectedLabelRef.current
        });
        setClickInformation({ position: clickCoords, value: clickPositiveRef.current ? 1 : 2 });
        clickHistoryRef.current = [...clickHistoryRef.current, { position: clickCoords, value: clickPositiveRef.current ? 1 : 2 }];
        setIsDownloadingImages(true);
        setProcessingClick(true);
        processingClickRef.current = true;
    }

    const handleUndo = () => {
        if (!clickSocket.current || !undoEnabled || processingClickRef.current) return;
        clickSocket.current.emit('undo');
        setProcessingClick(true);
        processingClickRef.current = true;
    }

    useEffect(() => {
        if (!clickSocket.current || selectedLabelRef.current === 0 || processingClickRef.current) {
            return;
        }
        sendClick();

    }, [clickCoords]);

    const startClickModule = async () => {
        if (clickSocket.current || startingClickModuleRef.current) return;
        // Kill any straggler from a previous session
        cleanupClickSocket();
        reconnectCyclesRef.current = 0;
        startingClickModuleRef.current = true;
        setLoadingClickModule(true);

        // Pre-flight: never construct a Manager / open a socket for a user with
        // no session.
        const preflightToken = await requireIdToken('start the correction session');
        startingClickModuleRef.current = false;
        if (cancelledRef.current) return;
        if (!preflightToken) {
            setLoadingClickModule(false);
            return;
        }

        // Consumed by the first auth-callback invocation; every reconnect after
        // that mints a fresh token.
        let firstConnectToken = preflightToken;

        /** @param {unknown} err */
        function handleUnrecoverableSocketFailure(err) {
            teardownCorrectionSocket(socket);
            if (cancelledRef.current) return;
            console.error('Correction socket failed:', /** @type {any} */ (err)?.message);
            setAuthErrorMessage(CORRECTION_SESSION_ENDED_MESSAGE);
            setIsAuthErrorSnackbarOpen(true);
        }

        const socket = io(segmentationApiUrl, {
            /**
             * socket.io calls this before EVERY connection attempt, including
             * automatic reconnections - so an idle tab whose ID token has
             * expired reconnects with a freshly minted one rather than the
             * stale token captured at session start.
             * @param {(data: object) => void} cb
             */
            auth: async (cb) => {
                if (cancelledRef.current || clickSocketInstanceRef.current !== socket) {
                    teardownCorrectionSocket(socket);
                    return;
                }
                if (reconnectCyclesRef.current >= CORRECTION_MAX_RECONNECT_CYCLES) {
                    handleUnrecoverableSocketFailure(new Error('reconnect cycle cap reached'));
                    return;
                }
                let token = firstConnectToken;
                firstConnectToken = null;
                if (!token) {
                    token = await requireIdToken('reconnect the correction session');
                    // The await widened the race window - re-check.
                    if (cancelledRef.current || clickSocketInstanceRef.current !== socket) {
                        teardownCorrectionSocket(socket);
                        return;
                    }
                }
                if (!token) {
                    // No valid token (dead/revoked refresh token)
                    handleUnrecoverableSocketFailure(new Error('token unavailable on reconnect'));
                    return;
                }
                cb({ user_id: userId, patient_id: patientId, token });
            },
            transports: ['websocket'],
            timeout: 120000,
            reconnectionAttempts: CORRECTION_SOCKET_RECONNECTION_ATTEMPTS,
        });
        clickSocket.current = socket;
        clickSocketInstanceRef.current = socket;

        socket.on('connect', () => {
            if (clickSocketInstanceRef.current !== socket) return;
            clickSocket.current = socket;
        });
        socket.on('ready', (/** @type {{ labels: number[] }} */ data) => {
            reconnectCyclesRef.current = 0;
            clickSocket.current = socket;
            setLabels(data.labels);
            setLoadingClickModule(false);
            setIsCorrecting(true);
            setCorrectionModuleLoaded(true);
            clickHistoryRef.current = [];
        });
        clickSocket.current.on('correction_done', async (/** @type {{history_count: number}} */ data) => {
            await reRenderSeg();
            setProcessingClick(false);
            processingClickRef.current = false;
            if (data.history_length > 0)
                setUndoEnabled(true);
            else
                setUndoEnabled(false);
        });
        clickSocket.current.on('undo_done', async (/** @type {{undone: boolean, history_count: number}} */ data) => {
            setProcessingClick(false);
            processingClickRef.current = false;
            clickHistoryRef.current.pop();
            setMarkerRedrawSignal((s) => s + 1);
            if (!data.undone || data.history_count <= 0)
                setUndoEnabled(false);
            else
                setUndoEnabled(true);
            await reRenderSeg();
        })
        socket.on('disconnect', (/** @type {string} */ reason) => {
            if (reason === 'io server disconnect') {
                handleUnrecoverableSocketFailure(new Error('server closed the correction session'));
                return;
            }
            reconnectCyclesRef.current += 1;
            clickSocket.current = null;
            resetInFlightClick();
        });
        socket.on('connect_error', (/** @type {unknown} */ err) => {
            if (socket.active) return;
            handleUnrecoverableSocketFailure(err);
        });
        // Manager-level event for the disjoint mode: transport-level reconnect
        // attempts exhausted (API down, network gone).
        const onReconnectFailed = () =>
            handleUnrecoverableSocketFailure(new Error('reconnect attempts exhausted'));
        reconnectFailedHandlerRef.current = onReconnectFailed;
        socket.io.on('reconnect_failed', onReconnectFailed);
    }

    const reRenderSeg = async () => {
        if (cancelledRef.current) return;
        setIsDownloadingImages(true);
        try {
            const segUrl = await getSegmentationUrl();
            if (cancelledRef.current || !segUrl) return;
            const seg = await NVImage.loadFromUrl({url: segUrl, ignoreZeroVoxels: true, opacity: 0.5, name: 'seg.nii.gz'});
            if (cancelledRef.current) return;
            seg.setColormapLabel(LUT);
            segVolRef.current = seg;
            setSegVolId(seg.id);
        } catch (err) {
            console.warn('reRenderSeg aborted or failed', err);
        } finally {
            if (!cancelledRef.current) setIsDownloadingImages(false);
        }
    }

    // when an error flags, open the dialog automatically (mimic old behaviour)
    useEffect(() => {
        if (isError) {
            setErrorDialogOpen(true);
        }
    }, [isError]);

    useEffect(() => {
        if (!authChecked || !patientResolved || patientId) {
            return;
        }
        navigate('/patients', {replace: true});
    }, [authChecked, patientResolved, patientId, navigate]);

    useEffect(() => {
        let cancelled = false;
        const renderImages = async () => {
            if (!segmentationDone || !userId || cancelled) return;
            setIsDownloadingImages(true);
            try {
                const [imagesUrl, segUrl] = await Promise.all([getImageUrl(), getSegmentationUrl()]);
                if (cancelled || !imagesUrl || !segUrl) return;
                const [image, seg] = await Promise.all([
                    NVImage.loadFromUrl({ url: imagesUrl, ignoreZeroVoxels: true, name: 'T2.nii.gz' }),
                    NVImage.loadFromUrl({ url: segUrl, ignoreZeroVoxels: true, opacity: 0.5, name: 'seg.nii.gz' }),
                ]);
                if (cancelled) return;

                imgVolRef.current = image;
                imgVolZerosLikeRef.current = NVImage.zerosLike(image);
                setImgVolId(image.id);

                seg.setColormapLabel(LUT);
                // debugger;
                segVolRef.current = seg;
                setSegVolId(seg.id);
            } catch (err) {
                console.warn('renderImages aborted or failed', err);
            } finally {
                if (!cancelled) setIsDownloadingImages(false);
            }
        };

        renderImages();
        return () => { cancelled = true; };
    }, [segmentationDone, userId, patientId, offlineImageUrl, offlineSegUrl]);

    useEffect(() => {
        let cancelled = false;
        const renderModel = async () => {
            try{
                if (!shouldLoadModel || !userId || cancelled) return;
                const glbLoader = new GLTFLoader();
                const modelUrl = await getModelGLBUrl();
                if (cancelled) return;
                glbLoader.setDRACOLoader(dracoLoader);
                glbLoader.load(modelUrl, (gltf) => {
                    if (cancelled) return;
                    const obj = gltf.scene.children[0];
                    if (cancelled) return;
                    objRef.current = obj;
                    setObjId(obj.uuid);
                })
            } catch (err) {
                console.warn('renderModel aborted or failed', err)
            } finally {
                if (!cancelled) setIsDownloadingModel(false);
            }
        };
        renderModel();
        return () => { cancelled = true; };
    }, [shouldLoadModel, userId, patientId, offlineModelUrl]);

    useEffect(() => {
        cancelledRef.current = false;
        const setup = async () => {
            if (running.current) {
                return
            }
            running.current = true
        };
        const loadLabelTemplate = async () => {
            try {
                const response = await fetch('shared-assets/label-legends/label_legend.json');
                if (!response.ok) return;
                const data = await response.json();
                labelTemplate.current = Object.fromEntries(Object.entries(data).map(a => a.reverse()));
            } catch (err) {
                console.warn('failed to load label template', err);
            }
        };
        const handleBeforeUnload = () => {
            cleanupClickSocket();
        };

        loadLabelTemplate();
        setup();
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            cancelledRef.current = true;
            window.removeEventListener('beforeunload', handleBeforeUnload);
            cleanupClickSocket();
            if (patientDocUnsubscribeRef.current)
                patientDocUnsubscribeRef.current();
            patientDocUnsubscribeRef.current = null;
        }
    }, []);

    return (
        <Box sx={{display: 'flex', width: '100%', height: '100vh', overflow: 'hidden'}}>
            <Card
                variant="outlined"
                sx={{
                    width: '100%',
                    height: '100%',
                    p: 1.15,
                    overflow: (maximized !== -1 && maximized !== 4) ? 'auto' : 'hidden',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <CardContent
                    sx={{
                        px: 0,
                        py: 1,
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: (maximized !== -1 && maximized !== 4) ? 'auto' : 'hidden',
                        '&:last-child': { pb: 1 }
                    }}
                >
                    <ProcessingErrorDialog
                        open={errorDialogOpen}
                        onClose={() => setErrorDialogOpen(false)}
                        patientId={patientId}
                        stepLabel={getErrorLabel(currentStepKey, currentStepLabel)}
                        errorType={currentStepKey === 'upload_failed' ? 'upload' : 'processing'}
                    />
                    <ProcessingErrorDialog
                        open={isDownloadErrorDialogOpen}
                        onClose={() => setIsDownloadErrorDialogOpen(false)}
                        patientId={patientId}
                        errorType="download"
                    />
                    <Dialog open={isValidateConfirmOpen} onClose={() => setIsValidateConfirmOpen(false)} disableScrollLock>
                        <DialogTitle>Confirm validation</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Are you sure you want to validate this segmentation? This action cannot be undone.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsValidateConfirmOpen(false)}>Cancel</Button>
                            <Button onClick={handleConfirmValidate} variant="contained">Confirm</Button>
                        </DialogActions>
                    </Dialog>
                    <Snackbar
                        open={isAuthErrorSnackbarOpen}
                        autoHideDuration={6000}
                        onClose={() => setIsAuthErrorSnackbarOpen(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={() => setIsAuthErrorSnackbarOpen(false)}
                            severity="error"
                            sx={{ width: '100%' }}
                        >
                            {authErrorMessage}
                        </Alert>
                    </Snackbar>
                    <Box sx={{ mb: 1 }}>
                        <BreadCrumbsWithIcons
                            items={/** @type {any} */ ([
                                { key: 'home', label: 'Home', to: '/dashboard', Icon: HomeOutlinedIcon },
                                { key: 'patients', label: 'Patients', to: '/patients', Icon: PeopleOutlineRoundedIcon },
                                {
                                    key: 'patient',
                                    label: (
                                        <>
                                            {patientName || ''}
                                            {patientName && isOfflineMode ? (
                                                <Box component="span" sx={{ fontWeight: 400 }}>
                                                    {' '}(Offline Mode)
                                                </Box>
                                            ) : null}
                                        </>
                                    ),
                                    to: null,
                                    Icon: PersonIcon,
                                },
                            ])}
                            sx={{ justifyContent: 'flex-start' }}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row' }}>
                        {!isDemoUser && segmentationDone && !segmentationValidated && (
                            <Box sx={{ flex: '0 0 auto', mr: 1, display: 'flex', flexDirection: 'column',
                                        height: '100%', maxHeight: '100%' }}>
                                <Stack justifyContent="left" spacing={2} useFlexGap sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 2 }}>
                                    <Button variant="outlined" onClick={startClickModule} disabled={loadingClickModule}>
                                        {loadingClickModule ? <CircularProgress size={30} /> : <EditIcon />}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        aria-label="Validate segmentation"
                                        onClick={() => setIsValidateConfirmOpen(true)}
                                        disabled={processingClick}
                                    >
                                        <DoneIcon />
                                    </Button>
                                    {correctionModuleLoaded &&
                                        <Button
                                            variant="outlined"
                                            aria-label="Undo last clicks"
                                            onClick={handleUndo}
                                            disabled={processingClick || !undoEnabled}
                                            hidden={!correctionModuleLoaded}>
                                            <UndoIcon/>
                                        </Button>
                                    }
                                        <ToggleButtonGroup
                                            value={clickPositive}
                                            exclusive
                                            aria-label="Click Type"
                                            onChange={handleClickTypeChange}
                                            hidden={!correctionModuleLoaded}
                                        >
                                            <ToggleButton
                                            value={true}
                                            aria-label="Positive"
                                            sx={selectedBackgroundSx(CORRECTION_POSITIVE_SELECTED_BG)}
                                        >
                                                <AddIcon />
                                            </ToggleButton>
                                            <ToggleButton
                                            value={false}
                                            aria-label="Negative"
                                            sx={selectedBackgroundSx(CORRECTION_NEGATIVE_SELECTED_BG)}
                                        >
                                                <RemoveIcon />
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                                <List sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                            {labels.map((label) => (
                                                <ListItemButton
                                                    key={labelTemplate.current?.[label] || label}
                                                    onClick={() => selectLabel(label)}
                                                    selected={selectedLabel === label}
                                                    sx={selectedBackgroundSx(CORRECTION_LABEL_SELECTED_BG)}
                                            >
                                                    <ListItemText
                                                        primary={labelTemplate.current?.[label] || label}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                </Stack>
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            {(() => {
                                if (!layoutReady) return (
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: 200,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 2,
                                        py: 3
                                    }}
                                >
                                    <CircularProgress size={56} thickness={4} disableShrink />
                                </Box>
                                );
                                if (isProcessingUnviewable) return (
                                    <PatientProcessingStepper
                                        currentStepKey={currentStepKey}
                                        percentage={percentage}
                                        currentStepLabel={currentStepLabel}
                                        isError={isError}
                                    />
                                );
                                if (isDemoUser) return (
                                    <>
                                        <Box sx={{ flex: (maximized !== -1 && maximized !== 4) ? '1 1 auto' : '0 0 auto', height: (maximized !== -1 && maximized !== 4) ? 'auto' : '38vh', minHeight: (maximized !== -1 && maximized !== 4) ? '600px' : '200px', mb: 1, display: maximized === 4 ? 'none' : 'block', overflow: (maximized !== -1 && maximized !== 4) ? 'auto' : 'visible' }}>
                                            <div className={`demo-three-viewports ${maximized === -1 ? 'compact' : 'expanded'}`}>
                                                <style>{`
                                                    .demo-three-viewports { padding: 0; width: 100%; height: 100%; }
                                                    .demo-three-viewports .MuiStack-root { width: 100%; height: 100%; }
                                                    .demo-three-viewports.compact .MuiStack-root { flex-wrap: nowrap !important; gap: min(1vw, 8px) !important; }
                                                    .demo-three-viewports.compact .demo-mri-slot {
                                                        flex: 1 1 33.333% !important;
                                                        min-width: 0 !important;
                                                        height: 100% !important;
                                                        box-sizing: border-box;
                                                    }
                                                    .demo-three-viewports.compact .demo-mri-slot section {
                                                        width: 100% !important;
                                                        height: 100% !important;
                                                        padding: 0 !important;
                                                    }
                                                    .demo-three-viewports.compact .demo-mri-slot section > div[style] {
                                                        width: 100% !important;
                                                        height: 100% !important;
                                                        max-width: 100% !important;
                                                    }
                                                    .demo-three-viewports.compact .demo-mri-slot .MuiIconButton-root {
                                                        top: min(2vw, 10px) !important;
                                                        right: min(2vw, 10px) !important;
                                                        padding: min(1.5vw, 8px) !important;
                                                    }
                                                    .demo-three-viewports.compact .demo-mri-slot .MuiIconButton-root svg {
                                                        font-size: clamp(14px, 2.5vw, 24px) !important;
                                                    }
                                                    .demo-three-viewports.expanded {
                                                        width: 100%;
                                                        height: auto;
                                                        min-height: 600px;
                                                        display: flex;
                                                        justify-content: center;
                                                        align-items: flex-start;
                                                    }
                                                    .demo-three-viewports.expanded .MuiStack-root {
                                                        width: auto;
                                                        height: auto;
                                                        justify-content: center;
                                                    }
                                                    .demo-three-viewports.expanded .demo-mri-slot {
                                                        width: auto !important;
                                                        height: auto !important;
                                                        max-width: none !important;
                                                    }
                                                    .demo-three-viewports.expanded .demo-mri-slot section {
                                                        width: auto !important;
                                                        height: auto !important;
                                                    }
                                                `}</style>

                                                <Stack
                                                    direction="row"
                                                    justifyContent={maximized === -1 ? 'flex-start' : 'center'}
                                                    useFlexGap
                                                    sx={{ flexWrap: 'nowrap', width: '100%', height: '100%' }}
                                                >
                                                    <div className="demo-mri-slot">
                                                        <MRIViewport
                                                            slice={SLICE_TYPE.AXIAL}
                                                            imageVolId={imgVolId}
                                                            segVolId={segVolId}
                                                            isDownloading={isDownloadingImages}
                                                            isCorrectionLoading={processingClick}
                                                            onUpdateComplete={/** @type {any} */ (handleCorrectionUpdateComplete)}
                                                            imageLoading={/** @type {any} */ (isDownloadingImages)}
                                                            segLoading={/** @type {any} */ (isDownloadingImages)}
                                                            maximized={maximized}
                                                            setMaximized={setMaximized}
                                                            imageVolRef={imgVolRef}
                                                            segVolRef={segVolRef}
                                                        />
                                                    </div>

                                                    <div className="demo-mri-slot">
                                                        <MRIViewport
                                                            slice={SLICE_TYPE.CORONAL}
                                                            imageVolId={imgVolId}
                                                            segVolId={segVolId}
                                                            isDownloading={isDownloadingImages}
                                                            isCorrectionLoading={processingClick}
                                                            onUpdateComplete={/** @type {any} */ (handleCorrectionUpdateComplete)}
                                                            imageLoading={/** @type {any} */ (isDownloadingImages)}
                                                            segLoading={/** @type {any} */ (isDownloadingImages)}
                                                            maximized={maximized}
                                                            setMaximized={setMaximized}
                                                            imageVolRef={imgVolRef}
                                                            segVolRef={segVolRef}
                                                        />
                                                    </div>

                                                    <div className="demo-mri-slot">
                                                        <MRIViewport
                                                            slice={SLICE_TYPE.SAGITTAL}
                                                            imageVolId={imgVolId}
                                                            segVolId={segVolId}
                                                            isDownloading={isDownloadingImages}
                                                            isCorrectionLoading={processingClick}
                                                            onUpdateComplete={/** @type {any} */ (handleCorrectionUpdateComplete)}
                                                            imageLoading={/** @type {any} */ (isDownloadingImages)}
                                                            segLoading={/** @type {any} */ (isDownloadingImages)}
                                                            maximized={maximized}
                                                            setMaximized={setMaximized}
                                                            imageVolRef={imgVolRef}
                                                            segVolRef={segVolRef}
                                                        />
                                                    </div>
                                                </Stack>
                                            </div>
                                        </Box>
                                        <Box sx={{ flex: 1, minHeight: 0, height: maximized === 4 ? '100%' : 'auto', display: (maximized !== -1 && maximized !== 4) ? 'none' : 'block' }}>
                                            <div className={`demo-three-3d ${maximized === -1 ? 'compact' : 'expanded'}`} style={{height: '100%'}}>
                                                <style>{`
                                                    .demo-three-3d { padding: 0; width: 100%; height: 100%; }
                                                    .demo-three-3d .MuiStack-root { width: 100%; height: 100%; justify-content: center; }
                                                    .demo-three-3d .MuiStack-root > div:first-child {
                                                        flex: none !important;
                                                        width: calc(66.666% - min(0.33vw, 2.66px)) !important;
                                                        max-width: none !important;
                                                        height: 100% !important;
                                                        box-sizing: border-box;
                                                    }
                                                    .demo-three-3d .MuiStack-root > div:last-child {
                                                        flex: 1 1 auto !important;
                                                        max-width: none !important;
                                                        height: 100% !important;
                                                        box-sizing: border-box;
                                                    }
                                                    .demo-three-3d .MuiStack-root > div:first-child canvas {
                                                        width: 100% !important;
                                                        height: 100% !important;
                                                    }
                                                    .demo-three-3d .MuiStack-root > div:last-child .MuiList-root {
                                                        max-height: 100% !important;
                                                        overflow: auto !important;
                                                    }
                                                    .demo-three-3d .MuiStack-root { gap: min(1vw, 8px) !important; }
                                                    .demo-three-3d .MuiIconButton-root {
                                                        top: min(2vw, 10px) !important;
                                                        right: min(2vw, 10px) !important;
                                                        padding: min(1.5vw, 8px) !important;
                                                    }
                                                    .demo-three-3d .MuiIconButton-root svg {
                                                        font-size: clamp(14px, 2.5vw, 24px) !important;
                                                    }
                                                    .demo-three-3d .MuiListItem-root {
                                                        padding: min(2vw, 8px) min(3vw, 16px) !important;
                                                    }
                                                    .demo-three-3d .MuiListItemText-primary {
                                                        font-size: clamp(10px, 1.8vw, 16px) !important;
                                                    }
                                                    .demo-three-3d .MuiListItemText-secondary {
                                                        font-size: clamp(8px, 1.4vw, 14px) !important;
                                                    }
                                                `}</style>

                                                <ThreeViewport
                                                    objId={objId}
                                                    maximized={maximized}
                                                    setMaximized={setMaximized}
                                                    objRef={objRef}
                                                    objLoading={/** @type {any} */ (isDownloadingModel)}
                                                    isDownloading={isDownloadingModel}
                                                />
                                            </div>
                                        </Box>
                                    </>
                                );
                                return (
                                    <>
                                        <Box
                                            sx={() => {
                                                const is3DMax = maximized === 4 && modelExported;
                                                const allowGrow = showFourViewportGrid || (maximized !== -1 && !is3DMax);
                                                return {
                                                    flex: allowGrow ? '1 1 auto' : '0 0 auto',
                                                    height: allowGrow ? '100%' : '38vh',
                                                    minHeight: allowGrow ? '600px' : '200px',
                                                    mb: 1,
                                                    display: is3DMax ? 'none' : 'block',
                                                    overflow: showFourViewportGrid ? 'hidden' : (allowGrow ? 'auto' : 'visible')
                                                };
                                            }}
                                        >
                                            <div className={`card-three-viewports ${maximized === -1 ? 'compact' : 'expanded'}${showFourViewportGrid ? ' four-grid' : ''}`}>
                                        <style>{`                    /* Base responsive container - use height-based sizing */
                    .card-three-viewports { padding: 0; width: 100%; height: 100%; }
                    .card-three-viewports .MuiStack-root { width: 100%; height: 100%; }
                    .card-three-viewports.compact .MuiStack-root { flex-wrap: nowrap !important; gap: min(1vw, 8px) !important; }

                    /* MRI slots take equal quarters when four present, thirds otherwise */
                    .card-three-viewports.compact .card-mri-slot { 
                        flex: 1 1 25% !important; 
                        min-width: 0 !important; 
                        height: 100% !important;
                        box-sizing: border-box; 
                    }

                    .card-three-viewports.compact.four-grid .MuiStack-root {
                        flex-wrap: wrap !important;
                        align-content: stretch;
                    }
                    .card-three-viewports.compact.four-grid .card-mri-slot {
                        flex: 0 0 calc((100% - min(1vw, 8px)) / 2) !important;
                        max-width: calc((100% - min(1vw, 8px)) / 2) !important;
                        height: calc((100% - min(1vw, 8px)) / 2) !important;
                    }

                    /* Force height-based sizing on MRI viewport containers */
                    .card-three-viewports.compact .card-mri-slot section {
                        width: 100% !important;
                        height: 100% !important;
                        padding: 0 !important;
                    }
                    .card-three-viewports.compact .card-mri-slot section > div[style] {
                        width: 100% !important;
                        height: 100% !important;
                        max-width: 100% !important;
                    }

                    /* Scale icon buttons proportionally */
                    .card-three-viewports.compact .card-mri-slot .MuiIconButton-root { 
                        top: min(2vw, 10px) !important; 
                        right: min(2vw, 10px) !important; 
                        padding: min(1.5vw, 8px) !important;
                    }
                    .card-three-viewports.compact .card-mri-slot .MuiIconButton-root svg {
                        font-size: clamp(14px, 2.5vw, 24px) !important;
                    }

                    /* Expanded state (when a 2D image is maximized) */
                    .card-three-viewports.expanded { 
                        width: 100%; 
                        height: auto; 
                        min-height: 600px;
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                    }
                    .card-three-viewports.expanded .MuiStack-root { 
                        width: auto; 
                        height: auto; 
                        justify-content: center;
                    }
                    .card-three-viewports.expanded .card-mri-slot {
                        width: auto !important;
                        height: auto !important;
                        max-width: none !important;
                    }
                    .card-three-viewports.expanded .card-mri-slot section {
                        width: auto !important;
                        height: auto !important;
                    }
                `}</style>

                                        <Stack
                                            direction="row"
                                            justifyContent={maximized === -1 ? 'flex-start' : 'center'}
                                            useFlexGap
                                            sx={{ flexWrap: showFourViewportGrid ? 'wrap' : 'nowrap', width: '100%', height: '100%' }}
                                        >
                                            <div className="card-mri-slot">
                                                <MRIViewport
                                                    slice={SLICE_TYPE.AXIAL}
                                                    imageVolId={imgVolId}
                                                    segVolId={segVolId}
                                                    isDownloading={isDownloadingImages}
                                                    isCorrectionLoading={processingClick}
                                                    onUpdateComplete={/** @type {any} */ (handleCorrectionUpdateComplete)}
                                                    maximized={maximized}
                                                    setMaximized={setMaximized}
                                                    imageVolRef={imgVolRef}
                                                    segVolRef={segVolRef}
                                                    setClickCoords={/** @type {any} */ (setClickCoords)}
                                                    clickInformation={/** @type {any} */ (clickInformation)}
                                                    clickHistory={clickHistoryRef}
                                                    markerRedrawSignal={markerRedrawSignal}
                                                    isCorrecting={isCorrecting}
                                                />
                                            </div>

                                            <div className="card-mri-slot">
                                                <MRIViewport
                                                    slice={SLICE_TYPE.CORONAL}
                                                    imageVolId={imgVolId}
                                                    segVolId={segVolId}
                                                    isDownloading={isDownloadingImages}
                                                    isCorrectionLoading={processingClick}
                                                    onUpdateComplete={/** @type {any} */ (handleCorrectionUpdateComplete)}
                                                    maximized={maximized}
                                                    setMaximized={setMaximized}
                                                    imageVolRef={imgVolRef}
                                                    segVolRef={segVolRef}
                                                    setClickCoords={/** @type {any} */ (setClickCoords)}
                                                    clickInformation={/** @type {any} */ (clickInformation)}
                                                    clickHistory={clickHistoryRef}
                                                    markerRedrawSignal={markerRedrawSignal}
                                                    isCorrecting={isCorrecting}
                                                />
                                            </div>

                                            <div className="card-mri-slot">
                                                <MRIViewport
                                                    slice={SLICE_TYPE.SAGITTAL}
                                                    imageVolId={imgVolId}
                                                    segVolId={segVolId}
                                                    isDownloading={isDownloadingImages}
                                                    isCorrectionLoading={processingClick}
                                                    onUpdateComplete={/** @type {any} */ (handleCorrectionUpdateComplete)}
                                                    maximized={maximized}
                                                    setMaximized={setMaximized}
                                                    imageVolRef={imgVolRef}
                                                    segVolRef={segVolRef}
                                                    setClickCoords={/** @type {any} */ (setClickCoords)}
                                                    clickInformation={/** @type {any} */ (clickInformation)}
                                                    clickHistory={clickHistoryRef}
                                                    markerRedrawSignal={markerRedrawSignal}
                                                    isCorrecting={isCorrecting}
                                                />
                                            </div>

                                            <div className="card-mri-slot" style={{ display: modelExported ? 'none' : 'block' }}>
                                                <MRIViewport
                                                    slice={SLICE_TYPE.RENDER}
                                                    imageVolId={imgVolId}
                                                    segVolId={segVolId}
                                                    isDownloading={isDownloadingImages}
                                                    isCorrectionLoading={processingClick}
                                                    onUpdateComplete={/** @type {any} */ (handleCorrectionUpdateComplete)}
                                                    maximized={maximized}
                                                    setMaximized={setMaximized}
                                                    imageVolRef={imgVolZerosLikeRef}
                                                    segVolRef={segVolRef}
                                                    hidden={modelExported}
                                                />
                                            </div>
                                        </Stack>
                                            </div>
                                        </Box>

                                        <Box
                                            sx={{
                                                flex: showFourViewportGrid ? 0 : 1,
                                                minHeight: 0,
                                                height: maximized === 4 ? '100%' : 'auto',
                                                display: (maximized !== -1 && maximized !== 4) || showFourViewportGrid ? 'none' : 'block'
                                            }}
                                        >
                                            <div
                                                className={`card-three-3d ${maximized === -1 ? 'compact' : 'expanded'}`}
                                                style={{ height: '100%' }}
                                            >
                                        <style>{`                    /* 3D viewport responsive styles - height-based */
                    .card-three-3d { padding: 0; width: 100%; height: 100%; }
                    .card-three-3d .MuiStack-root { width: 100%; height: 100%; justify-content: center; }
                    
                    /* 3D canvas width aligned with divider between 2nd and 3rd MRI (66.666% - gap/3) */
                    .card-three-3d .MuiStack-root > div:first-child { 
                        flex: none !important; 
                        width: calc(66.666% - min(0.33vw, 2.66px)) !important; 
                        max-width: none !important; 
                        height: 100% !important;
                        box-sizing: border-box; 
                    }
                    
                    .card-three-3d .MuiStack-root > div:last-child { 
                        flex: 1 1 auto !important; 
                        max-width: none !important; 
                        height: 100% !important; 
                        box-sizing: border-box; 
                    }
                    
                    /* Canvas fills its container */
                    .card-three-3d .MuiStack-root > div:first-child canvas { 
                        width: 100% !important; 
                        height: 100% !important; 
                    }
                    
                    /* Options list scrolls within its container */
                    .card-three-3d .MuiStack-root > div:last-child .MuiList-root { 
                        max-height: 100% !important; 
                        overflow: auto !important; 
                    }
                    
                    .card-three-3d .MuiStack-root { gap: min(1vw, 8px) !important; }

                    /* Scale icon buttons and sliders proportionally */
                    .card-three-3d .MuiIconButton-root { 
                        top: min(2vw, 10px) !important; 
                        right: min(2vw, 10px) !important;
                        padding: min(1.5vw, 8px) !important;
                    }
                    .card-three-3d .MuiIconButton-root svg {
                        font-size: clamp(14px, 2.5vw, 24px) !important;
                    }
                    .card-three-3d .MuiListItem-root {
                        padding: min(2vw, 8px) min(3vw, 16px) !important;
                    }
                    .card-three-3d .MuiListItemText-primary {
                        font-size: clamp(10px, 1.8vw, 16px) !important;
                    }
                    .card-three-3d .MuiListItemText-secondary {
                        font-size: clamp(8px, 1.4vw, 14px) !important;
                    }
                `}</style>

                                                <ThreeViewport
                                                    objId={objId}
                                                    maximized={maximized}
                                                    setMaximized={setMaximized}
                                                    objRef={objRef}
                                                    isDownloading={isDownloadingModel}
                                                    hidden={!modelExported}
                                                />
                                            </div>
                                        </Box>
                                    </>
                                );
                            })()}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
